import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import {
  Trophy, Play, ArrowsClockwise, X, TrendUp, TrendDown,
  Warning, CheckCircle, XCircle, Fire, Timer, Target, Lightning, Calendar,
  Ranking, MegaphoneSimple, Coins, LockKey, Scales, Share, Plus, Check, PencilSimple,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import MobilePortfolioSheet from "../components/MobilePortfolioSheet";
import { hasBinanceStream, subscribeLivePrice as subscribeLivePriceCex } from "../lib/binanceStreamer";
import { hasDhanStream, subscribeLivePrice as subscribeLivePriceMcx } from "../lib/dhanStreamer";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import TradingViewChart from "../components/TradingViewChart";
import LiveChart from "../components/LiveChart";
import TvAdvancedChart from "../components/TvAdvancedChart";

// Flip REACT_APP_TV_ADVANCED_CHART=1 in frontend/.env once the TradingView
// Advanced Charts library has been dropped into /public/charting_library/.
const USE_TV_ADVANCED = process.env.REACT_APP_TV_ADVANCED_CHART === "1";
import PropTutorial, { shouldShowTutorial } from "../components/PropTutorial";
import { BrandLogo } from "../components/BrandLogo";

// -------------- Utility --------------
const fmt = (n, d = 2) =>
  n === null || n === undefined || isNaN(n) ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: d });

const money = (n, sign = false) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
  const s = sign && v >= 0 ? "+$" : v < 0 ? "−$" : "$";
  return s + abs;
};

// -------------- Rule toast --------------
// Translates 429 / 412 / 423 API rejections from the MFTC engine into
// branded, icon-first toasts instead of the raw error string.
const RULE_MAP = {
  429: { title: "Trade cap hit", tone: "amber" },
  412: { title: "Margin cap breached", tone: "amber" },
  423: { title: "News lock active", tone: "red" },
  402: { title: "Upgrade required", tone: "blue" },
  400: { title: "Order rejected", tone: "amber" },
};
function ruleToast(err, fallback = "Order failed") {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail || fallback;
  const cfg = RULE_MAP[status];
  if (!cfg) return toast.error(detail);
  const toneCls = {
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-500",
    red: "border-red-500/40 bg-red-500/10 text-red-500",
    blue: "border-blue-500/40 bg-blue-500/10 text-blue-500",
  }[cfg.tone];
  const IconMap = { 429: Timer, 412: Scales, 423: LockKey, 402: LockKey, 400: Warning };
  const Icon = IconMap[status] || Warning;
  toast.custom((id) => (
    <div
      data-testid={`rule-toast-${status}`}
      className={`min-w-[320px] max-w-[420px] rounded-xl border px-4 py-3 backdrop-blur-md shadow-xl flex items-start gap-3 ${toneCls}`}
    >
      <Icon size={22} weight="fill" className="shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold opacity-90">MFTC Rule · {status}</p>
        <p className="font-semibold mt-0.5">{cfg.title}</p>
        <p className="text-xs opacity-90 mt-1 leading-relaxed">{detail}</p>
      </div>
      <button onClick={() => toast.dismiss(id)} className="opacity-60 hover:opacity-100 mt-0.5" aria-label="close">
        <X size={14} weight="bold" />
      </button>
    </div>
  ), { duration: 6000 });
}

// -------------- Trader name form --------------
const COUNTRIES = [
  "United States", "United Kingdom", "India", "Canada", "Australia", "Germany",
  "France", "United Arab Emirates", "Singapore", "Nigeria", "South Africa",
  "Brazil", "Mexico", "Japan", "Netherlands", "Sweden", "Switzerland", "Other",
];
function TraderNameForm({ open, defaults, challengeRules, onSubmit, onCancel, submitting }) {
  const [name, setName] = useState(defaults?.name || "");
  const [country, setCountry] = useState(defaults?.country || "");
  const [handle, setHandle] = useState(defaults?.handle || "");
  const [rulesAgreed, setRulesAgreed] = useState(false);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    setName(defaults?.name || "");
    setCountry(defaults?.country || "");
    setHandle(defaults?.handle || "");
    setRulesAgreed(false);
    setTouched(false);
  }, [defaults, open]);
  if (!open) return null;

  // All three fields are mandatory — the certificate + leaderboard rely on them.
  const errName = name.trim().length < 2;
  const errCountry = !country;
  const errHandle = handle.trim().length < 2;
  const errRules = !rulesAgreed;
  const canSubmit = !errName && !errCountry && !errHandle && !errRules && !submitting;
  const attempt = () => {
    setTouched(true);
    if (canSubmit) onSubmit({ trader_name: name.trim(), country, handle: handle.trim(), rules_accepted: true });
  };

  const r = challengeRules || {};
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto" data-testid="trader-name-modal">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-gradient-to-br from-neutral-950 to-neutral-900 p-6 shadow-2xl my-8">
        <div className="flex items-center gap-2 mb-1">
          <BrandLogo size={16} wordmark={false} />
          <p className="text-[10px] tracking-[0.25em] uppercase text-primary/80">Certificate identity</p>
        </div>
        <h3 className="font-display text-2xl font-bold text-white">Sign your challenge</h3>
        <p className="text-sm text-neutral-400 mt-1">
          All three fields are printed on your certificate and shared P&L card. Fill them in to start the challenge.
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Trader name<span className="text-red-500 ml-1">*</span></Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder="e.g. John Carter"
              data-testid="trader-name-input"
              className={`mt-1 bg-black/50 text-white ${touched && errName ? "border-red-500/60" : "border-white/10"}`} />
            {touched && errName && <p className="text-[11px] text-red-400 mt-1">Enter at least 2 characters</p>}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Country<span className="text-red-500 ml-1">*</span></Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="trader-country-select"
                className={`mt-1 bg-black/50 text-white ${touched && errCountry ? "border-red-500/60" : "border-white/10"}`}>
                <SelectValue placeholder="Pick your country" />
              </SelectTrigger>
              <SelectContent className="z-[200] max-h-[300px]">
                {COUNTRIES.map(cc => (
                  <SelectItem key={cc} value={cc} data-testid={`trader-country-${cc.replace(/\s+/g, '-')}`}>{cc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {touched && errCountry && <p className="text-[11px] text-red-400 mt-1">Please pick a country</p>}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Handle / trading alias<span className="text-red-500 ml-1">*</span></Label>
            <div className={`mt-1 flex items-center gap-1 rounded-md bg-black/50 border px-2.5 ${touched && errHandle ? "border-red-500/60" : "border-white/10"}`}>
              <span className="text-neutral-500 text-sm">@</span>
              <input
                value={handle}
                onChange={(e) => setHandle(e.target.value.replace(/^@/, ""))}
                maxLength={40}
                placeholder="tradingalpha"
                data-testid="trader-handle-input"
                className="w-full h-9 bg-transparent text-white text-sm focus:outline-none" />
            </div>
            {touched && errHandle && <p className="text-[11px] text-red-400 mt-1">Enter your trading handle (min 2 chars)</p>}
          </div>

          {/* Rules acceptance — required for ALL challenges */}
          <div className={`rounded-xl border p-4 ${touched && errRules ? "border-red-500/60 bg-red-500/[0.03]" : "border-white/10 bg-black/30"}`}>
            <p className="text-[10px] uppercase tracking-widest text-primary/80 font-bold mb-2">Challenge rules — please read</p>
            <ul className="text-[12px] text-neutral-300 space-y-1 leading-relaxed max-h-[180px] overflow-y-auto pr-1">
              <li>• <b>Profit target</b>: {r.profit_target_pct || 0}% of starting balance to pass</li>
              <li>• <b>Max daily loss</b>: {r.max_daily_loss_pct || 0}% of daily start equity — breach = fail</li>
              <li>• <b>Max total drawdown</b>: {r.max_total_drawdown_pct || 0}% from {r.hwm_drawdown ? "high-water mark" : "starting balance"} — breach = fail</li>
              <li>• <b>Min trading days</b>: {r.min_trading_days || 0} unique days before payout eligibility</li>
              <li>• <b>Deadline</b>: {r.deadline_days ? `${r.deadline_days} calendar days` : "no deadline"}</li>
              {r.max_trades_per_day && <li>• <b>Max trades / day</b>: {r.max_trades_per_day} (open positions counted)</li>}
              <li>• <b>News trading</b>: {r.no_news_trading ? "blocked ±2 min of high-impact events" : "allowed"}</li>
              <li>• <b>Overnight holds</b>: {r.no_overnight ? "not allowed" : "allowed"}</li>
              <li>• <b>Weekend holds</b>: not allowed on FX/metals/indices</li>
              <li>• <b>Leverage</b>: chosen per order — XAUUSD 20-500x · BTCUSD 50-400x · other 20-200x</li>
              <li>• <b>Paper trading</b>: virtual money, no real risk, no real payout — for learning &amp; certification only</li>
            </ul>
            <label className="mt-3 flex items-start gap-2 cursor-pointer text-sm text-white">
              <input
                type="checkbox"
                checked={rulesAgreed}
                onChange={(e) => setRulesAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-white/20 bg-black/50 accent-primary"
                data-testid="rules-agree-checkbox"
              />
              <span className="leading-snug">
                I have read and agree to all the challenge rules above.
                Breaching a rule fails the challenge automatically.
              </span>
            </label>
            {touched && errRules && <p className="text-[11px] text-red-400 mt-2">You must accept the rules before starting</p>}
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid="trader-form-cancel"
            className="text-neutral-400 hover:text-white">Cancel</Button>
          <Button
            onClick={attempt}
            disabled={submitting}
            data-testid="trader-form-submit"
            className="ml-auto rounded-full bg-primary hover:bg-primary/90">
            <Play size={14} weight="fill" className="mr-1.5" />
            {submitting ? "Starting…" : "Start challenge"}
          </Button>
        </div>
      </div>
    </div>
  );
}


// -------------- Landing (no active challenge) --------------
function Landing({ firms, onStart, starting }) {
  const [firmId, setFirmId] = useState(firms[0]?.id || "");
  const firm = firms.find(f => f.id === firmId);
  const [chId, setChId] = useState(firm?.challenges?.[0]?.id || "");
  useEffect(() => { setChId(firm?.challenges?.[0]?.id || ""); }, [firmId, firm]);
  const ch = firm?.challenges?.find(c => c.id === chId);

  return (
    <div data-testid="propsim-landing">
      <div className="rounded-2xl overflow-hidden border border-border relative"
        style={{
          background:
            "radial-gradient(120% 120% at 100% 0%, hsla(var(--primary)/0.15) 0%, transparent 50%)," +
            "linear-gradient(145deg, hsl(var(--card)) 0%, hsl(var(--background)) 100%)",
        }}>
        <div className="p-5 sm:p-8 lg:p-10">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-semibold uppercase tracking-widest mb-3">
            <Lightning size={11} weight="fill" /> Paper trading · Virtual money
          </div>
          <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight">
            Tradelog Prop Firm Challenge
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2 sm:mt-3 max-w-2xl">
            Learn to trade under real prop firm rules — profit target, daily loss limit, max drawdown,
            minimum trading days, deadline — all enforced live, with zero real money at risk.
            Pick a challenge template that mirrors the firm you're targeting and hit Start.
          </p>
        </div>
      </div>

      <div className="mt-6 sm:mt-8 grid lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-1 rounded-xl border border-border bg-card p-4 sm:p-6">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">Pick a firm</p>
          <div className="grid grid-cols-2 gap-2">
            {firms.map(f => (
              <button
                key={f.id}
                onClick={() => setFirmId(f.id)}
                data-testid={`propsim-firm-${f.id}`}
                className={`h-14 rounded-lg border text-sm font-semibold transition-all ${
                  firmId === f.id
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-secondary/40 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                }`}>
                {f.name}
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              {firm?.name} · Pick a challenge template
            </p>
            <a href={firm?.site} target="_blank" rel="noopener noreferrer"
              className="text-[10px] text-muted-foreground hover:text-foreground">
              Official rules ↗
            </a>
          </div>
          <div className="space-y-2 max-h-[380px] overflow-auto hide-scroll pr-1">
            {firm?.challenges?.map(c => (
              <label key={c.id}
                data-testid={`propsim-challenge-${c.id}`}
                className={`flex items-center justify-between gap-3 p-3 rounded-lg cursor-pointer border transition-all ${
                  chId === c.id
                    ? "border-primary bg-primary/5"
                    : "border-border bg-secondary/30 hover:border-primary/30"
                }`}>
                <div className="flex items-center gap-3 min-w-0">
                  <input
                    type="radio"
                    checked={chId === c.id}
                    onChange={() => setChId(c.id)}
                    className="accent-primary" />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.label}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {c.phase} · Target {c.rules.profit_target_pct}% · Daily {c.rules.max_daily_loss_pct}% · DD {c.rules.max_total_drawdown_pct}%
                      {c.rules.min_trading_days ? ` · Min ${c.rules.min_trading_days}d` : ""}
                      {c.rules.deadline_days ? ` · ${c.rules.deadline_days}d deadline` : " · No deadline"}
                    </p>
                  </div>
                </div>
                <p className="font-mono-num font-bold text-lg text-primary shrink-0">
                  ${fmt(c.start_balance, 0)}
                </p>
              </label>
            ))}
          </div>

          {ch && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
              <RuleTile label="Balance"       val={`$${fmt(ch.start_balance, 0)}`} />
              <RuleTile label="Target"        val={`${ch.rules.profit_target_pct}%`} />
              <RuleTile label="Daily loss"    val={`${ch.rules.max_daily_loss_pct}%`} />
              <RuleTile label="Max DD"        val={`${ch.rules.max_total_drawdown_pct}%${ch.rules.hwm_drawdown ? " · HWM" : ""}`} />
              <RuleTile label="Min days"      val={ch.rules.min_trading_days || 0} />
            </div>
          )}

          <Button
            onClick={() => onStart(firmId, chId)}
            disabled={!chId || starting}
            className="mt-5 rounded-full"
            data-testid="propsim-start-btn">
            <Play size={14} weight="fill" className="mr-1.5" />
            {starting ? "Starting…" : "Start Tradelog Prop Firm Challenge"}
          </Button>
        </div>
      </div>

      <PastCertificates />
    </div>
  );
}

function PastCertificates() {
  const [certs, setCerts] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/prop-sim/certificates");
      setCerts(data.certificates || []);
    } catch { /* silent — landing renders without this section */ }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (certs === null && !busy) return null;
  if (certs && certs.length === 0) return null;

  return (
    <div className="mt-6 sm:mt-8 rounded-xl border border-border bg-card p-4 sm:p-6" data-testid="past-certs-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Your certificates</p>
          <h3 className="font-display text-lg font-semibold">Past challenges &amp; downloads</h3>
        </div>
        {busy && <p className="text-xs text-muted-foreground">Loading…</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(certs || []).map(c => {
          const passed = c.status === "passed";
          const badge = passed
            ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/30"
            : "bg-red-500/15 text-red-500 border-red-500/30";
          const pdfUrl = `${API}/prop-sim/shared/${c.token}/certificate.pdf`;
          const viewUrl = `/prop-challenge/share/${c.token}`;
          return (
            <div key={c.token} className="rounded-lg border border-border bg-secondary/40 p-3 sm:p-4 flex flex-col gap-2"
              data-testid={`past-cert-${c.token}`}>
              <div className="flex items-start justify-between gap-2 min-w-0">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate text-foreground">{c.firm_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.challenge_label} · ${Number(c.starting_balance).toLocaleString()}</p>
                </div>
                <span className={`text-[9px] px-2 py-0.5 rounded-full border ${badge} font-semibold uppercase shrink-0`}>
                  {c.status}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground font-mono">
                <span>{c.cert_id}</span>
                <span className="opacity-40">·</span>
                <span className={`${passed ? "text-emerald-400" : "text-red-400"}`}>
                  {(Number(c.pct_return) >= 0 ? "+" : "") + Number(c.pct_return).toFixed(2)}%
                </span>
                <span className="opacity-40">·</span>
                <span>{(c.started_at || "").slice(0, 10)}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <a href={viewUrl} target="_blank" rel="noopener noreferrer"
                  className="flex-1 text-center text-xs py-1.5 rounded-md border border-border hover:bg-background text-foreground"
                  data-testid={`past-cert-view-${c.token}`}>
                  View
                </a>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                  className="flex-1 text-center text-xs py-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary font-semibold border border-primary/25"
                  data-testid={`past-cert-download-${c.token}`}>
                  Download PDF
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RuleTile({ label, val }) {
  return (
    <div className="rounded-lg border border-border/60 bg-secondary/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-mono-num text-sm font-bold mt-0.5">{val}</p>
    </div>
  );
}

// -------------- Active dashboard --------------
function StatusPill({ status }) {
  const map = {
    passed:   { icon: CheckCircle, cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30", label: "PASSED" },
    failed:   { icon: XCircle,     cls: "bg-red-500/15 text-red-500 border-red-500/30",         label: "FAILED" },
    active:   { icon: Fire,        cls: "bg-blue-500/15 text-blue-500 border-blue-500/30",      label: "LIVE" },
    abandoned:{ icon: X,           cls: "bg-neutral-500/15 text-neutral-400 border-neutral-500/30", label: "ABANDONED" },
  };
  const s = map[status] || map.active;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold border ${s.cls}`} data-testid="propsim-status-pill">
      <Icon size={12} weight="fill" /> {s.label}
    </span>
  );
}

function ProgressCard({ title, value, target, pct, tone, subtitle, testid, icon: Icon }) {
  const bar = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500", blue: "bg-blue-500" };
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={11} className="text-muted-foreground" />}
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{title}</p>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-mono-num text-2xl font-bold">{value}</span>
        <span className="text-[11px] text-muted-foreground">{target}</span>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full ${bar[tone]} transition-all duration-500`}
          style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
      {subtitle && <p className="text-[11px] text-muted-foreground mt-2">{subtitle}</p>}
    </div>
  );
}

function OrderPanel({ symbols, activeState, onOrderPlaced, symbol, setSymbol,
                     openTrades = [], pendingOrders = [],
                     onCloseAll, onCloseLosing, onCloseProfitable }) {
  const [orderType, setOrderType] = useState("market"); // market | limit
  const [side, setSide] = useState("long");
  const [qty, setQty] = useState("0.10");
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [limitPx, setLimitPx] = useState("");
  const [leverage, setLeverage] = useState(null); // null → use symbol default (max)
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [newsLock, setNewsLock] = useState(null);

  const meta = symbols.find(s => s.symbol === symbol);
  // Reset leverage to symbol's max whenever the traded symbol changes.
  useEffect(() => {
    if (meta) setLeverage(meta.leverage_max || meta.leverage || 200);
  }, [meta?.symbol]); // eslint-disable-line react-hooks/exhaustive-deps
  const levMin = meta?.leverage_min || 20;
  const levMax = meta?.leverage_max || 200;
  const effLev = Math.max(levMin, Math.min(levMax, Number(leverage || levMax)));

  const fetchQuote = useCallback(async () => {
    if (!symbol) return;
    try {
      const { data } = await api.get(`/prop-sim/quote?symbol=${symbol}`);
      setQuote(data);
    } catch { /* noop */ }
  }, [symbol]);

  const fetchNewsLock = useCallback(async () => {
    if (!symbol) return;
    try {
      const { data } = await api.get(`/prop-sim/news-lock?symbol=${symbol}`);
      setNewsLock(data);
    } catch { /* noop */ }
  }, [symbol]);

  useEffect(() => { fetchQuote(); fetchNewsLock(); }, [fetchQuote, fetchNewsLock]);
  useEffect(() => {
    // REST poll for non-streamed symbols (forex/indices via TwelveData) and
    // to keep spread / currency metadata fresh even for streamed ones.
    const t1 = setInterval(() => {
      if (document.visibilityState === "visible") fetchQuote();
    }, 8000);
    const t2 = setInterval(() => {
      if (document.visibilityState === "visible") fetchNewsLock();
    }, 60000);
    const onVis = () => {
      if (document.visibilityState === "visible") { fetchQuote(); fetchNewsLock(); }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(t1); clearInterval(t2);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchQuote, fetchNewsLock]);

  // Subscribe to the SAME live-tick stream the chart uses so the order
  // panel price NEVER drifts behind the candle the user is looking at.
  // - BTC / ETH / XAU / XAG → Coinbase / Binance WS (binanceStreamer pool)
  // - MCX_GOLD / SILVER / CRUDEOIL → Dhan relay (via our backend)
  // For symbols in neither pool we fall back to the 4-second REST poll
  // above (TwelveData forex / indices).
  useEffect(() => {
    if (!symbol) return;
    let unsub = null;
    if (hasBinanceStream(symbol)) {
      unsub = subscribeLivePriceCex(symbol, (price) => {
        setQuote(q => ({ ...(q || {}), symbol, price }));
      });
    } else if (hasDhanStream(symbol)) {
      unsub = subscribeLivePriceMcx(symbol, "USD", (price) => {
        setQuote(q => ({ ...(q || {}), symbol, price }));
      });
    }
    return () => { if (unsub) unsub(); };
  }, [symbol]);

  const q = Number(qty || 0);
  const entryPreview = quote?.price;
  const slNum = Number(sl || 0);
  const riskDollars = useMemo(() => {
    if (!slNum || !entryPreview || !q || !meta) return null;
    const diff = side === "long" ? entryPreview - slNum : slNum - entryPreview;
    if (diff <= 0) return null;
    return diff * q * meta.contract_size;
  }, [slNum, entryPreview, q, meta, side]);
  const riskPct = riskDollars && activeState
    ? (riskDollars / activeState.starting_balance) * 100 : null;

  const dailyHeadroom = activeState
    ? activeState.limits.max_daily_loss + Math.min(0, activeState.today_pnl)
    : 0;
  const wouldBreachDaily = riskDollars && riskDollars > dailyHeadroom;

  const isLocked = !!newsLock?.locked;
  const nextEvent = newsLock?.next_event;

  const place = async (overrideSide) => {
    const useSide = overrideSide || side;
    if (isLocked) return toast.error("News lock — try again after the release");
    if (!symbol || !q) return toast.error("Enter a valid quantity");
    setBusy(true);
    try {
      if (orderType === "limit") {
        const lp = Number(limitPx || 0);
        if (!lp) throw { response: { data: { detail: "Enter a limit price" } } };
        const { data } = await api.post("/prop-sim/limit-order", {
          symbol, side: useSide, quantity: q, limit_price: lp,
          stop_loss: slNum || null, take_profit: Number(tp || 0) || null,
          leverage: effLev,
        });
        toast.success(`Limit ${useSide.toUpperCase()} ${q} ${symbol} @ ${fmt(lp, meta.decimals)} placed`);
        setLimitPx("");
        onOrderPlaced?.(data.state);
      } else {
        const { data } = await api.post("/prop-sim/order", {
          symbol, side: useSide, quantity: q,
          stop_loss: slNum || null, take_profit: Number(tp || 0) || null,
          leverage: effLev,
        });
        toast.success(`Opened ${useSide.toUpperCase()} ${q} ${symbol} @ ${fmt(data.trade.entry_price, meta.decimals)}`);
        onOrderPlaced?.(data.state);
      }
      setSl(""); setTp("");
    } catch (e) {
      ruleToast(e, "Order failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="propsim-order-panel">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Place order</p>
        <p className="text-[10px] text-muted-foreground font-mono">{quote ? `${meta?.name}` : "—"}</p>
      </div>

      <div className="mt-3 flex items-center justify-between p-3 rounded-lg bg-secondary border border-border">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{symbol} · Live</p>
          <p className="font-mono-num text-3xl font-bold mt-0.5 text-foreground" data-testid="propsim-live-price">
            {quote ? fmt(quote.price, meta?.decimals || 4) : "—"}
          </p>
        </div>
        <button onClick={fetchQuote} className="text-muted-foreground hover:text-primary" title="Refresh">
          <ArrowsClockwise size={16} />
        </button>
      </div>

      {/* Market / Limit tabs */}
      <div className="mt-3 grid grid-cols-2 gap-1 p-1 bg-secondary rounded-lg" data-testid="propsim-order-type">
        {["market","limit"].map(t => (
          <button key={t}
            onClick={() => setOrderType(t)}
            data-testid={`propsim-ot-${t}`}
            className={`h-8 rounded text-xs font-semibold uppercase tracking-widest transition-colors ${
              orderType === t ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"
            }`}>
            {t === "market" ? "Market" : "Limit"}
          </button>
        ))}
      </div>
      {orderType === "limit" && (
        <div className="mt-3">
          <Label className="text-[10px] uppercase tracking-widest">Limit price</Label>
          <Input value={limitPx} onChange={e => setLimitPx(e.target.value)}
            type="number" step={meta ? Math.pow(10, -meta.decimals).toFixed(meta.decimals) : "0.0001"}
            placeholder={quote ? String(quote.price) : ""}
            data-testid="propsim-limit-price" />
          <p className="text-[10px] text-muted-foreground mt-1">
            {Number(limitPx) > 0 && quote ? (
              Number(limitPx) < quote.price
                ? `Buy limit fills when price drops to ${limitPx}`
                : `Sell limit fills when price rises to ${limitPx}`
            ) : "Order rests until price crosses your limit"}
          </p>
        </div>
      )}

      {isLocked && (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-2 text-xs text-red-500" data-testid="propsim-news-lock-active">
          <LockKey size={16} weight="fill" className="shrink-0 mt-0.5" />
          <div>
            <p className="font-bold uppercase text-[10px] tracking-widest">News lock — trading blocked</p>
            <p className="mt-0.5">
              <b>{newsLock.blocking_event.title}</b> ({newsLock.blocking_event.currency}) hits in{" "}
              <b>{Math.max(0, Math.round(newsLock.blocking_event.seconds_until / 60))}m {Math.abs(newsLock.blocking_event.seconds_until) % 60}s</b>.
              Prop firms restrict trading around high-impact releases.
            </p>
          </div>
        </div>
      )}
      {!isLocked && nextEvent && nextEvent.seconds_until < 3600 && (
        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2 text-xs text-amber-500" data-testid="propsim-news-warning">
          <MegaphoneSimple size={14} weight="fill" className="shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Heads up: <b>{nextEvent.title}</b> ({nextEvent.currency}) in {Math.round(nextEvent.seconds_until / 60)}m</p>
            <p className="text-muted-foreground mt-0.5">Orders will auto-lock 2 min before + 2 min after.</p>
          </div>
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Symbol</Label>
          <Select value={symbol} onValueChange={setSymbol}>
            <SelectTrigger className="bg-secondary text-foreground" data-testid="propsim-symbol-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Forex", "Metals", "Energy", "Crypto", "Indices"].map(cat => (
                <React.Fragment key={cat}>
                  <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-muted-foreground">{cat}</div>
                  {symbols.filter(s => s.category === cat).map(s => (
                    <SelectItem key={s.symbol} value={s.symbol} data-testid={`propsim-sym-${s.symbol}`}>
                      {s.symbol} · {s.name}
                    </SelectItem>
                  ))}
                </React.Fragment>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Volume (lots)</Label>
          <div className="flex items-center gap-1">
            <button onClick={() => setQty(String(Math.max(meta?.min_qty || 0.01, +(Number(qty) - (meta?.step || 0.01)).toFixed(2))))}
              className="w-8 h-10 rounded-md bg-secondary hover:bg-secondary/70 text-foreground text-lg font-bold" data-testid="propsim-qty-dec">−</button>
            <Input value={qty} onChange={e => setQty(e.target.value)}
              type="number" step={meta?.step || "0.01"} min={meta?.min_qty || 0.01}
              className="text-center font-mono-num bg-secondary" data-testid="propsim-qty" />
            <button onClick={() => setQty(String(+(Number(qty) + (meta?.step || 0.01)).toFixed(2)))}
              className="w-8 h-10 rounded-md bg-secondary hover:bg-secondary/70 text-foreground text-lg font-bold" data-testid="propsim-qty-inc">+</button>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Stop loss (optional)</Label>
          <Input value={sl} onChange={e => setSl(e.target.value)}
            type="number" step={meta ? Math.pow(10, -meta.decimals).toFixed(meta.decimals) : "0.0001"}
            placeholder={entryPreview ? String(entryPreview) : ""}
            className="bg-secondary"
            data-testid="propsim-sl" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Take profit (optional)</Label>
          <Input value={tp} onChange={e => setTp(e.target.value)}
            type="number" step={meta ? Math.pow(10, -meta.decimals).toFixed(meta.decimals) : "0.0001"}
            placeholder={entryPreview ? String(entryPreview) : ""}
            className="bg-secondary"
            data-testid="propsim-tp" />
        </div>
      </div>

      {riskDollars !== null && (
        <div className={`mt-3 rounded-lg border p-2.5 text-xs flex items-center gap-2 ${
          wouldBreachDaily ? "border-red-500/30 bg-red-500/5 text-red-500"
          : riskPct > 2 ? "border-amber-500/30 bg-amber-500/5 text-amber-500"
          : "border-emerald-500/30 bg-emerald-500/5 text-emerald-500"
        }`} data-testid="propsim-risk-preview">
          {wouldBreachDaily ? <Warning size={14} weight="fill" /> : <Target size={14} />}
          <span>
            Risk on this trade: <b>{money(riskDollars)}</b> · <b>{riskPct.toFixed(2)}%</b> of balance
            {wouldBreachDaily && " — WOULD breach daily loss limit"}
          </span>
        </div>
      )}

      {/* One-tap Sell / Buy price boxes — mirror the reference broker UI */}
      <div className="mt-3 grid grid-cols-2 gap-2" data-testid="propsim-onetap">
        <button
          onClick={() => { setSide("short"); place("short"); }}
          disabled={busy || !symbol || !q || isLocked}
          className="group h-24 rounded-xl bg-gradient-to-br from-red-500 to-red-600 hover:from-red-500 hover:to-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-left px-4 py-3 relative overflow-hidden transition-all"
          data-testid="propsim-onetap-sell">
          <p className="text-[10px] uppercase tracking-[0.2em] opacity-70">{orderType === "limit" ? "Sell Limit" : "Sell"}</p>
          <p className="font-mono-num font-black text-3xl tracking-tight mt-0.5">
            {orderType === "limit"
              ? (limitPx || (quote ? fmt(quote.price, meta?.decimals || 4) : "—"))
              : (quote ? fmt(quote.price, meta?.decimals || 4) : "—")}
          </p>
          <TrendDown size={40} weight="fill" className="absolute right-2 bottom-2 opacity-20 group-hover:opacity-30" />
        </button>
        <button
          onClick={() => { setSide("long"); place("long"); }}
          disabled={busy || !symbol || !q || isLocked}
          className="group h-24 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-500 hover:to-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-right px-4 py-3 relative overflow-hidden transition-all"
          data-testid="propsim-onetap-buy">
          <p className="text-[10px] uppercase tracking-[0.2em] opacity-70">{orderType === "limit" ? "Buy Limit" : "Buy"}</p>
          <p className="font-mono-num font-black text-3xl tracking-tight mt-0.5">
            {orderType === "limit"
              ? (limitPx || (quote ? fmt(quote.price, meta?.decimals || 4) : "—"))
              : (quote ? fmt(quote.price, meta?.decimals || 4) : "—")}
          </p>
          <TrendUp size={40} weight="fill" className="absolute left-2 bottom-2 opacity-20 group-hover:opacity-30" />
        </button>
      </div>

      {/* Buy / Sell sentiment bar — mirrors the classic broker "42% / 58%"
          strip. We compute it from the current user's own open lots for
          this symbol (sums of long vs short). If flat, show 50/50 to keep
          the visual anchored — same behavior Exness has on a fresh symbol. */}
      {(() => {
        const openHere = (openTrades || []).filter(t => t.symbol === symbol && t.status === "open");
        const longQty  = openHere.filter(t => t.side === "long").reduce((s, t) => s + Number(t.quantity || 0), 0);
        const shortQty = openHere.filter(t => t.side === "short").reduce((s, t) => s + Number(t.quantity || 0), 0);
        const total = longQty + shortQty;
        const shortPct = total > 0 ? Math.round((shortQty / total) * 100) : 50;
        const longPct  = total > 0 ? 100 - shortPct : 50;
        return (
          <div className="mt-2 flex items-center gap-1.5" data-testid="propsim-sentiment-bar" title="Your open lots long vs short on this symbol">
            <span className="text-[10px] font-mono font-bold text-red-400 min-w-[26px]">{shortPct}%</span>
            <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/[0.04] flex">
              <div className="h-full bg-red-500/70 transition-all" style={{ width: `${shortPct}%` }} />
              <div className="h-full bg-blue-500/70 transition-all" style={{ width: `${longPct}%` }} />
            </div>
            <span className="text-[10px] font-mono font-bold text-blue-400 min-w-[26px] text-right">{longPct}%</span>
          </div>
        );
      })()}

      {/* Fees / Leverage / Margin — decorative info like the reference */}
      <div className="mt-3 text-[11px] text-muted-foreground space-y-0.5 font-mono" data-testid="propsim-order-info">
        <div className="flex justify-between"><span>Fees</span><span>≈ ${((meta?.contract_size || 1) * q * 0.00003).toFixed(2)}</span></div>
        <div className="flex justify-between items-center gap-2">
          <span>Leverage <span className="text-[10px] opacity-70">(range 1:{levMin} – 1:{levMax})</span></span>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={levMin}
              max={levMax}
              step={levMax >= 1000 ? 50 : levMin >= 50 ? 10 : 5}
              value={effLev}
              onChange={(e) => setLeverage(Number(e.target.value))}
              className="w-28 accent-primary"
              data-testid="propsim-leverage-slider"
              title={`Chosen leverage: 1:${effLev}`}
            />
            <span className="font-mono-num text-foreground font-bold min-w-[42px] text-right" data-testid="propsim-order-leverage">1:{effLev}</span>
          </div>
        </div>
        <div className="flex justify-between"><span>Margin</span><span>${((entryPreview || 0) * q * (meta?.contract_size || 1) / effLev).toFixed(2)}</span></div>
      </div>

      <p className="mt-3 text-[10px] text-center text-muted-foreground uppercase tracking-widest">
        or use classic order button ↓
      </p>
      <Button
        onClick={() => place()}
        disabled={busy || !symbol || !q || isLocked}
        className="mt-2 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
        data-testid="propsim-place-order">
        {isLocked ? (
          <><LockKey size={14} className="mr-1.5" /> Locked — news release imminent</>
        ) : busy ? "Placing…" : `Market ${side === "long" ? "buy" : "sell"} ${q} ${symbol}`}
      </Button>

      {/* Manage positions — bulk actions always visible in the order panel */}
      <div className="mt-4 pt-4 border-t border-border" data-testid="propsim-manage-positions">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Manage positions</p>
          <p className="text-[10px] text-muted-foreground font-mono">
            {openTrades.length} open{pendingOrders.length ? ` · ${pendingOrders.length} pending` : ""}
          </p>
        </div>
        {(() => {
          const winners = openTrades.filter(t => (t.unrealized_pnl || 0) >= 0).length;
          const losers = openTrades.length - winners;
          return (
            <div className="space-y-1.5">
              <button
                onClick={onCloseProfitable}
                disabled={winners === 0}
                data-testid="propsim-panel-close-profitable"
                className="w-full h-9 rounded-md flex items-center justify-between px-3 text-xs font-semibold border border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <span className="flex items-center gap-1.5"><TrendUp size={12} weight="fill" /> Close winners</span>
                <span className="font-mono-num">{winners}</span>
              </button>
              <button
                onClick={onCloseLosing}
                disabled={losers === 0}
                data-testid="propsim-panel-close-losing"
                className="w-full h-9 rounded-md flex items-center justify-between px-3 text-xs font-semibold border border-red-500/30 text-red-500 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <span className="flex items-center gap-1.5"><TrendDown size={12} weight="fill" /> Close losers</span>
                <span className="font-mono-num">{losers}</span>
              </button>
              <button
                onClick={onCloseAll}
                disabled={openTrades.length === 0}
                data-testid="propsim-panel-close-all"
                className="w-full h-9 rounded-md flex items-center justify-between px-3 text-xs font-semibold border border-border text-foreground hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <span className="flex items-center gap-1.5"><X size={12} weight="bold" /> Close all positions</span>
                <span className="font-mono-num">{openTrades.length}</span>
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function PartialCloseButton({ trade, onClose }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const step = 0.01;
  const doClose = async (pct) => {
    if (busy) return;
    setBusy(true);
    setOpen(false);
    const rawQty = pct >= 100 ? null : Math.max(step, Math.round(trade.quantity * (pct / 100) * 100) / 100);
    try { await onClose(trade.id, rawQty); } finally { setBusy(false); }
  };
  return (
    <div className="relative inline-block" ref={ref}>
      <Button size="sm" variant="outline" onClick={() => setOpen(v => !v)} disabled={busy}
        className="h-7 rounded-full text-xs" data-testid={`propsim-close-${trade.id}`}>
        Close ▾
      </Button>
      {open && (
        <div className="absolute right-0 bottom-full mb-1 z-50 w-40 rounded-lg border border-border bg-card shadow-2xl overflow-hidden"
          data-testid={`propsim-close-menu-${trade.id}`}>
          {[25, 50, 75, 100].map(pct => {
            const closeQ = pct >= 100 ? trade.quantity : Math.max(step, Math.round(trade.quantity * (pct / 100) * 100) / 100);
            return (
              <button key={pct}
                onClick={() => doClose(pct)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-secondary border-b border-border last:border-0"
                data-testid={`propsim-close-${trade.id}-${pct}`}>
                <span className={pct >= 100 ? "font-semibold text-red-400" : ""}>{pct === 100 ? "Close all" : `Close ${pct}%`}</span>
                <span className="font-mono-num text-muted-foreground">{fmt(closeQ, 2)} lots</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OpenPositions({ open, meta, onClose, onCloseAll, onCloseLosing, onCloseProfitable }) {
  const [modify, setModify] = useState(null); // { trade, initialField }
  // Global listener — chart's + SL/TP button can dispatch this event to
  // open the Exness Modify dialog on a specific trade without any prop
  // wiring, so the two UIs stay in sync.
  useEffect(() => {
    const handler = (e) => {
      const tradeId = e?.detail?.tradeId;
      if (!tradeId) return;
      const t = (open || []).find(x => x.id === tradeId);
      if (t) setModify({ trade: t, initialField: "stop_loss" });
    };
    window.addEventListener("tj:open-modify", handler);
    return () => window.removeEventListener("tj:open-modify", handler);
  }, [open]);
  if (!open || open.length === 0) return (
    <div className="rounded-xl border border-dashed border-border bg-card/40 p-8 text-center text-sm text-muted-foreground" data-testid="propsim-open-empty">
      No open positions. Place your first order to see them here.
    </div>
  );
  const winners = open.filter(t => (t.unrealized_pnl || 0) >= 0).length;
  const losers = open.length - winners;

  const patchSltp = async (tradeId, field, value) => {
    try {
      const body = { [field]: value === null ? null : Number(value) };
      const { data } = await api.post(`/prop-sim/trade/${tradeId}/sltp`, body);
      toast.success(`${field === "stop_loss" ? "SL" : "TP"} ${value === null ? "cleared" : `→ ${value}`}. Chart line appears in ~3s.`);
      try { window.dispatchEvent(new CustomEvent("tj:positions-updated")); } catch (_) {}
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border gap-2 flex-wrap">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Open positions ({open.length})</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button size="sm" variant="outline"
            onClick={onCloseProfitable}
            disabled={winners === 0}
            className="rounded-full text-xs h-7 border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10 disabled:opacity-40"
            data-testid="propsim-close-profitable">
            <TrendUp size={12} weight="fill" className="mr-1" /> Close winners ({winners})
          </Button>
          <Button size="sm" variant="outline"
            onClick={onCloseLosing}
            disabled={losers === 0}
            className="rounded-full text-xs h-7 border-red-500/40 text-red-500 hover:bg-red-500/10 disabled:opacity-40"
            data-testid="propsim-close-losing">
            <TrendDown size={12} weight="fill" className="mr-1" /> Close losers ({losers})
          </Button>
          <Button size="sm" variant="outline" onClick={onCloseAll} className="rounded-full text-xs h-7" data-testid="propsim-close-all">
            Close all
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground border-b border-border">
              <th className="px-4 py-2">Symbol</th>
              <th className="px-4 py-2">Side</th>
              <th className="px-4 py-2 text-right">Lots</th>
              <th className="px-4 py-2 text-right">Entry</th>
              <th className="px-4 py-2 text-right">Current</th>
              <th className="px-4 py-2 text-right">SL</th>
              <th className="px-4 py-2 text-right">TP</th>
              <th className="px-4 py-2 text-right">Unrealized</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {open.map(t => {
              const m = meta.find(s => s.symbol === t.symbol);
              const positive = t.unrealized_pnl >= 0;
              const decimals = m?.decimals ?? 4;
              const cur = Number(t.current_price || t.entry_price);
              // Default distance: 0.5% of price — sensible for all
              // instruments (crypto, forex, MCX). Users can drag it on
              // the chart afterwards to their real level.
              const bump = cur * 0.005;
              const isLong = t.side === "long";
              const defaultSL = isLong ? cur - bump : cur + bump;
              const defaultTP = isLong ? cur + bump : cur - bump;
              return (
                <tr key={t.id} className="border-b border-border/60" data-testid={`propsim-open-row-${t.id}`}>
                  <td className="px-4 py-2 font-medium">{t.symbol}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isLong ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500"}`}>
                      {t.side.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmt(t.quantity, 2)}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmt(t.entry_price, decimals)}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmt(t.current_price, decimals)}</td>
                  {/* SL column — inline input or "+ Add" pill */}
                  <td className="px-4 py-2 text-right">
                    {t.stop_loss ? (
                      <button
                        onClick={() => setModify({ trade: t, initialField: "stop_loss" })}
                        className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 font-mono-num text-xs"
                        title="Click to modify SL"
                        data-testid={`propsim-sl-${t.id}`}>
                        {fmt(t.stop_loss, decimals)}
                      </button>
                    ) : (
                      <button
                        onClick={() => setModify({ trade: t, initialField: "stop_loss" })}
                        className="text-[10px] px-2 py-0.5 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10"
                        data-testid={`propsim-add-sl-${t.id}`}>
                        + Add SL
                      </button>
                    )}
                  </td>
                  {/* TP column */}
                  <td className="px-4 py-2 text-right">
                    {t.take_profit ? (
                      <button
                        onClick={() => setModify({ trade: t, initialField: "take_profit" })}
                        className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 font-mono-num text-xs"
                        title="Click to modify TP"
                        data-testid={`propsim-tp-${t.id}`}>
                        {fmt(t.take_profit, decimals)}
                      </button>
                    ) : (
                      <button
                        onClick={() => setModify({ trade: t, initialField: "take_profit" })}
                        className="text-[10px] px-2 py-0.5 rounded-md border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        data-testid={`propsim-add-tp-${t.id}`}>
                        + Add TP
                      </button>
                    )}
                  </td>
                  <td className={`px-4 py-2 text-right font-mono-num font-bold ${positive ? "text-emerald-500" : "text-red-500"}`}>
                    {money(t.unrealized_pnl, true)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <PartialCloseButton trade={t} onClose={onClose} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {modify && (
        <ModifyPositionDialog
          trade={modify.trade}
          meta={meta}
          onClose={() => setModify(null)}
          onSave={patchSltp}
          onClosePosition={onClose}
        />
      )}
    </div>
  );
}

// Exness-style Modify Position dialog. Opens from Open Positions +Add
// SL/TP pills OR from the chart's + SL/TP button (via tj:open-modify event).
function ModifyPositionDialog({ trade, meta, onClose, onSave, onClosePosition }) {
  const t = trade;
  const m = (meta || []).find(s => s.symbol === t.symbol) || {};
  const decimals = m.decimals ?? 4;
  const isLong = t.side === "long";
  const cur = Number(t.current_price || t.entry_price);
  const bump = cur * 0.005;
  const step = decimals === 0 ? 1 : Math.pow(10, -decimals);
  const [tab, setTab] = useState("modify");
  const [tp, setTp] = useState(t.take_profit ?? Number((isLong ? cur + bump : cur - bump).toFixed(decimals)));
  const [sl, setSl] = useState(t.stop_loss ?? Number((isLong ? cur - bump : cur + bump).toFixed(decimals)));
  const [partialPct, setPartialPct] = useState(50);
  const [busy, setBusy] = useState(false);
  const positive = (t.unrealized_pnl || 0) >= 0;
  const currencyMultiplier = m.currency === "INR" ? (1 / 89) : 1;
  // Compute running $ P&L a level would realize if hit right now.
  const estPnl = (px) => {
    if (!Number(px)) return 0;
    const diff = isLong ? (px - t.entry_price) : (t.entry_price - px);
    return diff * t.quantity * (m.contract_size || 1) * currencyMultiplier;
  };
  const tpEst = estPnl(tp), slEst = estPnl(sl);
  const commit = async () => {
    setBusy(true);
    const patches = [];
    if (Number(sl) !== Number(t.stop_loss)) patches.push(onSave(t.id, "stop_loss", Number(sl) || null));
    if (Number(tp) !== Number(t.take_profit)) patches.push(onSave(t.id, "take_profit", Number(tp) || null));
    await Promise.all(patches);
    setBusy(false);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-[300] grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="propsim-modify-dialog">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-4 border-b border-border">
          <div className={`w-1 h-10 rounded-full ${isLong ? "bg-emerald-500" : "bg-red-500"}`} />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-base">{t.symbol}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              <span className={isLong ? "text-emerald-500" : "text-red-500"}>{t.side.toUpperCase()}</span> · {fmt(t.quantity, 2)} lots · entry {fmt(t.entry_price, decimals)}
            </p>
          </div>
          <p className={`font-mono-num font-bold text-sm ${positive ? "text-emerald-500" : "text-red-500"}`}>{money(t.unrealized_pnl, true)}</p>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-white/[0.06]" data-testid="propsim-modify-close-x"><X size={16} /></button>
        </div>
        <div className="flex border-b border-border">
          {[["modify","Modify"],["partial","Partial close"]].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex-1 py-2.5 text-xs font-semibold uppercase tracking-wider ${tab===k?"text-primary border-b-2 border-primary":"text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        {tab === "modify" ? (
          <div className="p-4 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Take profit</label>
              <div className="flex items-center gap-1.5 mt-1.5">
                <button onClick={() => setTp(Number((Number(tp)-step).toFixed(decimals)))} className="w-8 h-9 rounded-md bg-secondary hover:bg-secondary/70 font-bold text-lg">−</button>
                <input type="number" step={step} value={tp || ""} onChange={(e) => setTp(Number(e.target.value))} className="flex-1 h-9 px-3 rounded-md bg-secondary/40 border border-border text-center font-mono-num text-emerald-400" data-testid="propsim-modify-tp-input" />
                <button onClick={() => setTp(Number((Number(tp)+step).toFixed(decimals)))} className="w-8 h-9 rounded-md bg-secondary hover:bg-secondary/70 font-bold text-lg">+</button>
              </div>
              {Number(tp) > 0 && <p className={`text-[10px] mt-1.5 font-mono ${tpEst >= 0 ? "text-emerald-400" : "text-red-400"}`}>{tpEst >= 0 ? "Reward" : "⚠ inverted"} · {money(tpEst, true)}</p>}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Stop loss</label>
              <div className="flex items-center gap-1.5 mt-1.5">
                <button onClick={() => setSl(Number((Number(sl)-step).toFixed(decimals)))} className="w-8 h-9 rounded-md bg-secondary hover:bg-secondary/70 font-bold text-lg">−</button>
                <input type="number" step={step} value={sl || ""} onChange={(e) => setSl(Number(e.target.value))} className="flex-1 h-9 px-3 rounded-md bg-secondary/40 border border-border text-center font-mono-num text-red-400" data-testid="propsim-modify-sl-input" />
                <button onClick={() => setSl(Number((Number(sl)+step).toFixed(decimals)))} className="w-8 h-9 rounded-md bg-secondary hover:bg-secondary/70 font-bold text-lg">+</button>
              </div>
              {Number(sl) > 0 && <p className={`text-[10px] mt-1.5 font-mono ${slEst < 0 ? "text-red-400" : "text-emerald-400"}`}>{slEst < 0 ? "Risk" : "⚠ inverted"} · {money(slEst, true)}</p>}
            </div>
            <Button onClick={commit} disabled={busy} className="w-full h-11 rounded-md bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm" data-testid="propsim-modify-save">{busy ? "Saving…" : "Modify position"}</Button>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Close portion</label>
            <input type="range" min={10} max={90} step={5} value={partialPct} onChange={(e) => setPartialPct(Number(e.target.value))} className="w-full accent-primary" data-testid="propsim-partial-slider" />
            <div className="flex items-center justify-between text-[10px] text-muted-foreground font-mono px-1">
              <span>10%</span><span>25%</span><span>50%</span><span>75%</span><span>90%</span>
            </div>
            <p className="text-center font-mono-num text-2xl font-bold" data-testid="propsim-partial-pct">{partialPct}%</p>
            <Button
              onClick={async () => {
                // Round to instrument step so backend accepts the qty. Default
                // step is 0.01 lots (2 dp) — matches every symbol in our meta.
                const step = 0.01;
                const closeQty = Math.max(step, Math.round(t.quantity * partialPct / 100 * 100) / 100);
                // Never let the slider send a qty ≥ full size (that would
                // become a full close). Cap at (quantity − step).
                const safeQty = Math.min(closeQty, Math.max(step, +(t.quantity - step).toFixed(2)));
                if (!window.confirm(`Close ${partialPct}% of this position (${fmt(safeQty, 2)} lots)?`)) return;
                setBusy(true);
                try {
                  // CRITICAL: pass qty as the 2nd arg — without it the
                  // backend defaults to a full close (the bug the user
                  // hit: "I picked 10 % but the whole position exited").
                  await onClosePosition(t.id, safeQty);
                  onClose();
                } finally { setBusy(false); }
              }}
              disabled={busy}
              className="w-full h-11 rounded-md bg-red-500 hover:bg-red-400 text-white font-bold text-sm disabled:opacity-50"
              data-testid="propsim-partial-close-btn">
              {busy ? "Closing…" : `Close ${partialPct}% (~${fmt(Math.max(0.01, Math.round(t.quantity * partialPct / 100 * 100) / 100), 2)} lots)`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ClosedTradesTable({ closed, meta }) {
  if (!closed || closed.length === 0) return null;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-5 py-3 border-b border-border">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Recent closed trades</p>
      </div>
      <div className="overflow-x-auto max-h-[320px] overflow-y-auto hide-scroll">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card">
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground border-b border-border">
              <th className="px-4 py-2">Time</th>
              <th className="px-4 py-2">Symbol</th>
              <th className="px-4 py-2">Side</th>
              <th className="px-4 py-2 text-right">Lots</th>
              <th className="px-4 py-2 text-right">Entry</th>
              <th className="px-4 py-2 text-right">Exit</th>
              <th className="px-4 py-2 text-right">P&amp;L</th>
            </tr>
          </thead>
          <tbody>
            {closed.map(t => {
              const m = meta.find(s => s.symbol === t.symbol);
              const positive = (t.pnl || 0) >= 0;
              return (
                <tr key={t.id} className="border-b border-border/60" data-testid={`propsim-closed-row-${t.id}`}>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{(t.exit_time || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2 font-medium">{t.symbol}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${t.side === "long" ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500"}`}>
                      {t.side.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmt(t.quantity, 2)}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmt(t.entry_price, m?.decimals || 4)}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmt(t.exit_price, m?.decimals || 4)}</td>
                  <td className={`px-4 py-2 text-right font-mono-num font-bold ${positive ? "text-emerald-500" : "text-red-500"}`}>
                    {money(t.pnl, true)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayoutCard({ p, firmName, challengeLabel }) {
  if (!p) return null;
  return (
    <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-card to-card p-6" data-testid="propsim-payout-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/40 grid place-items-center">
          <Coins size={16} weight="fill" className="text-emerald-500" />
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-emerald-500 font-bold">Passed — payout projection</p>
          <p className="text-xs text-muted-foreground">Estimated at your pace on {firmName} {challengeLabel}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Funded balance</p>
          <p className="font-mono-num text-2xl font-bold mt-1">{money(p.funded_balance)}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Your split</p>
          <p className="font-mono-num text-2xl font-bold mt-1">{p.split_pct}%</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">up to {p.max_split_pct}% w/ scaling</p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Projected month P&amp;L</p>
          <p className={`font-mono-num text-2xl font-bold mt-1 ${p.projected_monthly_profit >= 0 ? "text-emerald-500" : "text-red-500"}`}>
            {money(p.projected_monthly_profit, true)}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{p.projected_monthly_return_pct}% / month pace</p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Your monthly take</p>
          <p className={`font-mono-num text-3xl font-black mt-1 ${p.trader_take_monthly >= 0 ? "text-emerald-500" : "text-red-500"}`} data-testid="propsim-payout-monthly">
            {money(p.trader_take_monthly, true)}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            up to {money(p.trader_take_monthly_max, true)} · {money(p.trader_take_yearly, true)}/yr
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><Timer size={11} /> first payout after {p.first_payout_days}d</span>
        {p.refund_on_pass && (
          <span className="flex items-center gap-1 text-emerald-500"><CheckCircle size={11} weight="fill" /> challenge fee refunded on pass</span>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground mt-3 italic">{p.note}</p>
      <CertificateAction />
    </div>
  );
}

function CertificateAction() {
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/prop-sim/share");
      setToken(data.token);
      const pdfUrl = `${API}/prop-sim/shared/${data.token}/certificate.pdf`;
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
      toast.success("Certificate ready — download opened in a new tab");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate certificate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-emerald-500/20 pt-4">
      <Button
        onClick={generate}
        disabled={busy}
        className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold"
        data-testid="propsim-download-certificate"
      >
        {busy ? "Preparing…" : (token ? "Re-download Certificate" : "Download Certificate (PDF)")}
      </Button>
      {token && (
        <a
          href={`${API}/prop-sim/shared/${token}/certificate.pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-emerald-400 underline underline-offset-4 hover:text-emerald-300"
          data-testid="propsim-certificate-direct-link"
        >
          Open PDF ↗
        </a>
      )}
      <span className="text-[11px] text-muted-foreground">
        Shareable · Includes trader name, firm, and verified result
      </span>
    </div>
  );
}

function Leaderboard({ compact = false }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/prop-sim/leaderboard?limit=${compact ? 8 : 20}`)
      .then(r => setRows(r.data.leaderboard || []))
      .catch(() => setRows([]));
  }, [compact]);

  if (rows === null) return (
    <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
      Loading leaderboard…
    </div>
  );
  if (rows.length === 0) return (
    <div className="rounded-xl border border-dashed border-border bg-card/40 p-6 text-center" data-testid="propsim-leaderboard-empty">
      <Ranking size={20} className="mx-auto text-muted-foreground mb-2" />
      <p className="text-sm font-medium">No one on the leaderboard yet</p>
      <p className="text-xs text-muted-foreground mt-1">Start a challenge to be first — every user's pace shows here.</p>
    </div>
  );

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="propsim-leaderboard">
      <div className="px-5 py-3 border-b border-border flex items-center gap-2">
        <Ranking size={14} className="text-primary" />
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Community leaderboard</p>
      </div>
      <div className="max-h-[440px] overflow-y-auto hide-scroll">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card">
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground border-b border-border">
              <th className="px-4 py-2">#</th>
              <th className="px-4 py-2">Trader</th>
              <th className="px-4 py-2">Firm · Tier</th>
              <th className="px-4 py-2 text-right">Return</th>
              <th className="px-4 py-2 text-right">Days</th>
              <th className="px-4 py-2 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const pos = r.pct_return >= 0;
              const targetMet = r.pct_return >= r.target_pct;
              return (
                <tr key={i} className="border-b border-border/60" data-testid={`propsim-lb-row-${i}`}>
                  <td className="px-4 py-2 font-mono-num text-xs text-muted-foreground">
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                  </td>
                  <td className="px-4 py-2 font-medium">{r.trader}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: r.firm_logo_color }} />
                      <span className="truncate">{r.firm_name} · <span className="text-muted-foreground">{r.challenge_label}</span></span>
                    </div>
                  </td>
                  <td className={`px-4 py-2 text-right font-mono-num font-bold ${pos ? "text-emerald-500" : "text-red-500"}`}>
                    {pos ? "+" : ""}{r.pct_return}%
                  </td>
                  <td className="px-4 py-2 text-right font-mono-num text-xs text-muted-foreground">
                    {r.days_traded}/{r.min_days || 0}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      r.status === "passed" ? "bg-emerald-500/15 text-emerald-500"
                      : targetMet ? "bg-blue-500/15 text-blue-500"
                      : "bg-secondary text-muted-foreground"
                    }`}>
                      {r.status === "passed" ? "PASSED" : targetMet ? "ON TRACK" : "LIVE"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConsistencyCard({ c }) {
  const pct = c.limit_pct
    ? Math.min(100, (c.best_day_share_pct / c.limit_pct) * 100)
    : 0;
  const tone = !c.ok ? "red" : pct >= 85 ? "amber" : "green";
  const bar = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="propsim-progress-consistency">
      <div className="flex items-center gap-1.5">
        <Scales size={11} className="text-muted-foreground" />
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Consistency</p>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`font-mono-num text-2xl font-bold ${!c.ok ? "text-red-500" : ""}`}>
          {c.total_profit > 0 ? `${c.best_day_share_pct}%` : "—"}
        </span>
        <span className="text-[11px] text-muted-foreground">of {c.limit_pct}% max</span>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full ${bar[tone]} transition-all duration-500`}
          style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">
        {c.total_profit <= 0
          ? "No winning day yet — pass one to start tracking"
          : c.ok
            ? `Best day ${c.best_day} = ${money(c.best_day_pnl, true)}. ${c.headroom_pct}% headroom left`
            : `Best day ${c.best_day} = ${money(c.best_day_pnl, true)} — trade smaller & spread wins`}
      </p>
    </div>
  );
}

function PendingOrders({ pending, meta, onCancel }) {
  if (!pending || pending.length === 0) return null;
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.04] overflow-hidden" data-testid="propsim-pending">
      <div className="px-5 py-3 border-b border-amber-500/20 flex items-center gap-2">
        <Timer size={14} className="text-amber-500" />
        <p className="text-[10px] tracking-[0.2em] uppercase text-amber-500 font-semibold">
          Pending limit orders ({pending.length})
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground border-b border-amber-500/20">
              <th className="px-4 py-2">Symbol</th>
              <th className="px-4 py-2">Side</th>
              <th className="px-4 py-2 text-right">Lots</th>
              <th className="px-4 py-2 text-right">Limit</th>
              <th className="px-4 py-2 text-right">SL / TP</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {pending.map(o => {
              const m = meta.find(s => s.symbol === o.symbol);
              return (
                <tr key={o.id} className="border-b border-amber-500/10" data-testid={`propsim-pending-row-${o.id}`}>
                  <td className="px-4 py-2 font-medium">{o.symbol}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${o.side === "long" ? "bg-blue-500/15 text-blue-400" : "bg-red-500/15 text-red-400"}`}>
                      {o.side.toUpperCase()} LIMIT
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmt(o.quantity, 2)}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmt(o.limit_price, m?.decimals || 4)}</td>
                  <td className="px-4 py-2 text-right text-xs text-muted-foreground font-mono">
                    {o.stop_loss ? fmt(o.stop_loss, m?.decimals || 4) : "—"} / {o.take_profit ? fmt(o.take_profit, m?.decimals || 4) : "—"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => onCancel(o.id)} className="h-7 rounded-full text-xs" data-testid={`propsim-cancel-${o.id}`}>
                      Cancel
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TradingTerminal({ symbols, symbol, setSymbol, livePnl, openTrades = [], pendingOrders = [], onStateUpdate }) {
  // Show only the requested set + a couple extras: XAUUSD, BTCUSD, ETHUSD,
  // EURUSD, GBPUSD, USDJPY, USDCAD, USOIL, AUDUSD, XAGUSD, AUDCAD
  const preferred = ["MCX_GOLD","MCX_SILVER","MCX_CRUDEOIL","XAUUSD","XAGUSD","BTCUSD","ETHUSD","EURUSD","GBPUSD","USDJPY","USDCAD","AUDUSD","AUDCAD","EURJPY"];
  const orderedTabs = [
    ...preferred.map(s => symbols.find(x => x.symbol === s)).filter(Boolean),
    // XAUUSD / XAGUSD are back on the desk (ThinkMarkets TV iframes) — stop
    // excluding them. USOIL + the index CFDs still route to their own TV
    // iframes elsewhere so keep excluding those.
    ...symbols.filter(s => !preferred.includes(s.symbol) && !["USOIL","US30","NAS100","SPX500","GER40"].includes(s.symbol)),
  ];
  const meta = symbols.find(s => s.symbol === symbol) || orderedTabs[0];
  const tvSymbol = meta?.tv || "FX:EURUSD";

  // Filter positions & pending limit orders for the currently viewed symbol
  const symbolPositions = openTrades.filter(t => t.symbol === symbol);
  const symbolPending = pendingOrders.filter(p => p.symbol === symbol);
  const decimals = meta?.decimals || 4;

  // Symbols that don't support 1m/3m (Dhan MCX feed + Binance Futures
  // metals). Chart opens at 5m by default for these and the tf pills below
  // are filtered so the user can't pick anything smaller.
  const CAP5M_SYMBOLS = new Set(["MCX_GOLD","MCX_SILVER","MCX_CRUDEOIL","XAUUSD","XAGUSD","USOIL"]);
  const _cap5m = CAP5M_SYMBOLS.has(symbol);
  const [interval, setInterval] = useState(_cap5m ? "5" : "1");
  // Whenever the user switches into a 5m-min symbol on a smaller TF, snap
  // to 5m so the chart is never stuck on a resolution the feed can't serve.
  useEffect(() => {
    if (_cap5m && (interval === "1" || interval === "3")) setInterval("5");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);
  const _allTimeframes = [
    { k: "1",   label: "1m" },
    { k: "3",   label: "3m" },
    { k: "5",   label: "5m" },
    { k: "15",  label: "15m" },
    { k: "30",  label: "30m" },
    { k: "60",  label: "1H" },
    { k: "120", label: "2H" },
    { k: "240", label: "4H" },
    { k: "D",   label: "D" },
    { k: "W",   label: "W" },
    { k: "M",   label: "M" },
  ];
  const timeframes = _cap5m ? _allTimeframes.filter(t => t.k !== "1" && t.k !== "3") : _allTimeframes;

  // ---- Symbol search — filters the tab strip
  const [symbolQuery, setSymbolQuery] = useState("");
  const filteredTabs = symbolQuery
    ? orderedTabs.filter(s =>
        s.symbol.toLowerCase().includes(symbolQuery.toLowerCase()) ||
        (s.name || "").toLowerCase().includes(symbolQuery.toLowerCase()))
    : orderedTabs;

  return (
    <div className="rounded-xl overflow-hidden border border-white/10 bg-neutral-950 h-full flex flex-col" data-testid="propsim-terminal">
      {/* Tradelog branded top bar with LIVE P&L badges on the right */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10 bg-black/60">
        <div className="flex items-center gap-1.5 shrink-0">
          <Lightning size={14} weight="fill" className="text-primary" />
          <span className="font-display font-bold text-white text-sm">Tradelog Trade Desk</span>
          <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] tracking-widest uppercase bg-primary/15 text-primary font-semibold">DEMO</span>
        </div>
        <div className="ml-auto flex items-center gap-2" data-testid="propsim-live-pnl">
          <PnlChip label="Today" value={livePnl?.today} />
          <PnlChip label="Open" value={livePnl?.open} />
          <PnlChip label="Total" value={livePnl?.total} big />
        </div>
      </div>
      {/* Symbol tabs strip — with search + horizontal scroll */}
      <div className="flex items-center gap-2 px-2 py-2 border-b border-white/10 bg-black/40">
        <div className="relative shrink-0">
          <input
            value={symbolQuery}
            onChange={(e) => setSymbolQuery(e.target.value)}
            placeholder="Search…"
            data-testid="propsim-symbol-search"
            className="h-8 w-32 rounded-md bg-white/[0.04] border border-white/10 text-white text-xs px-2.5 placeholder:text-neutral-500 focus:outline-none focus:border-white/30" />
        </div>
        <div className="flex-1 flex gap-0.5 overflow-x-auto hide-scroll" data-testid="propsim-symbol-tabs">
          {filteredTabs.length === 0 && (
            <div className="text-[11px] text-neutral-500 px-2 py-1.5" data-testid="propsim-symbol-no-match">
              No symbols match "{symbolQuery}"
            </div>
          )}
          {filteredTabs.map(s => {
            const active = s.symbol === symbol;
            return (
              <button
                key={s.symbol}
                onClick={() => setSymbol(s.symbol)}
                data-testid={`propsim-tab-${s.symbol}`}
                className={`shrink-0 flex items-center gap-1.5 px-2.5 h-8 rounded-md text-xs font-semibold transition-all ${
                  active
                    ? "bg-white/10 text-white ring-1 ring-white/20"
                    : "text-neutral-400 hover:text-white hover:bg-white/[0.04]"
                }`}>
                <span className="text-sm leading-none">{s.flag}</span>
                <span className="font-mono tracking-wide">{s.symbol}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Chart — flex-1 so it fills whatever height the parent grants.
          Uses our own Lightweight-Charts feed by default; swaps in the real
          TradingView Advanced Charts widget when the library is present and
          the REACT_APP_TV_ADVANCED_CHART flag is on.
          Note: the standalone timeframe strip we used to render above was
          removed on user request — the TradingView widget already ships its
          own interval selector, and the wedge is enough for Lightweight
          Charts mode (it uses the default "60" interval). */}
      <div className="relative flex-1 min-h-[560px] h-full">
        {USE_TV_ADVANCED
          ? <TvAdvancedChart symbol={symbol} interval={interval} height="100%" onIntervalChange={setInterval} />
          : <LiveChart symbol={symbol} interval={interval} height="100%" />}
        {/* Floating "+ SL/TP" trigger — sits on top-right of chart, opens quick-add popover */}
        <SLTPQuickAdd
          symbol={symbol}
          decimals={decimals}
          positions={symbolPositions}
          pendings={symbolPending}
          onStateUpdate={onStateUpdate}
        />
        {/* Floating live P&L badge overlay bottom-right of the chart */}
        <div className="pointer-events-none absolute top-3 right-3 flex flex-col gap-1 items-end" data-testid="propsim-chart-pnl-overlay">
          <div className={`px-2.5 py-1 rounded-md text-xs font-bold backdrop-blur-md border ${
            (livePnl?.total ?? 0) >= 0
              ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
              : "bg-red-500/20 border-red-500/40 text-red-300"
          }`}>
            {(livePnl?.total ?? 0) >= 0 ? "+" : ""}${(livePnl?.total ?? 0).toFixed(2)}
          </div>
          <div className="px-2 py-0.5 rounded text-[10px] font-mono bg-black/60 text-white/70 border border-white/10">
            {livePnl?.open != null && livePnl.open !== 0 ? `open ${(livePnl.open >= 0 ? "+" : "") + livePnl.open.toFixed(2)}` : "no open"}
          </div>
        </div>

        {/* Position Levels overlay REMOVED — the TradingView Advanced Charts
            widget now draws native `createPositionLine` + `createOrderLine`
            ribbons directly on the chart (see TvAdvancedChart.jsx →
            drawActivePositionLine), so the absolute-positioned card here is
            redundant and was cluttering the top-left of the chart. */}
      </div>
    </div>
  );
}

function PnlChip({ label, value, big = false }) {
  const v = value ?? 0;
  const pos = v >= 0;
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-md ${big ? "bg-white/10" : "bg-white/[0.03]"} border border-white/10`}>
      <span className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</span>
      <span className={`font-mono-num ${big ? "text-sm font-bold" : "text-xs"} ${pos ? "text-emerald-400" : "text-red-400"}`}>
        {pos ? "+" : ""}${v.toFixed(2)}
      </span>
    </div>
  );
}

/**
 * A single horizontal line with a coloured square + label + price value.
 * Emulates a chart "level" line for open positions when the free TradingView
 * embed doesn't expose createShape.
 */
function LevelBar({ color, label, price, decimals = 4, dashed = false }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px]">
      <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: color }} />
      <span className="text-white/60 font-semibold w-9">{label}</span>
      <div className="flex-1 h-px" style={{
        background: dashed
          ? `repeating-linear-gradient(90deg, ${color} 0 4px, transparent 4px 8px)`
          : color,
        opacity: 0.7,
      }} />
      <span className="font-mono-num font-bold text-white shrink-0">{fmt(price, decimals)}</span>
    </div>
  );
}

/**
 * Floating "+ SL/TP" quick-add popover pinned to the top-right of the chart.
 * Opens a small form to set/edit stop-loss & take-profit on any open position
 * or pending limit order for the currently-viewed symbol.
 */
function SLTPQuickAdd({ symbol, decimals, positions = [], pendings = [], onStateUpdate }) {
  const [open, setOpen] = useState(false);
  const targets = useMemo(() => [
    ...positions.map(p => ({
      kind: "position",
      id: p.id,
      label: `${p.side === "long" ? "LONG" : "SHORT"} ${fmt(p.quantity, 2)} @ ${fmt(p.entry_price, decimals)}`,
      side: p.side,
      ref_price: p.entry_price,
      stop_loss: p.stop_loss,
      take_profit: p.take_profit,
    })),
    ...pendings.map(p => ({
      kind: "pending",
      id: p.id,
      label: `${p.side === "long" ? "BUY LIMIT" : "SELL LIMIT"} ${fmt(p.quantity, 2)} @ ${fmt(p.limit_price, decimals)}`,
      side: p.side,
      ref_price: p.limit_price,
      stop_loss: p.stop_loss,
      take_profit: p.take_profit,
    })),
  ], [positions, pendings, decimals]);

  const [targetId, setTargetId] = useState(targets[0]?.id || "");
  useEffect(() => {
    if (!targets.find(t => t.id === targetId)) setTargetId(targets[0]?.id || "");
  }, [targets, targetId]);
  const target = targets.find(t => t.id === targetId);

  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  useEffect(() => {
    setSl(target?.stop_loss != null ? String(target.stop_loss) : "");
    setTp(target?.take_profit != null ? String(target.take_profit) : "");
  }, [target?.id, target?.stop_loss, target?.take_profit]);

  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!target) return;
    const parseNum = (v) => {
      const s = String(v).trim();
      if (!s) return null;
      const n = Number(s);
      return isNaN(n) || n <= 0 ? null : n;
    };
    const body = { stop_loss: parseNum(sl), take_profit: parseNum(tp) };
    const url = target.kind === "position"
      ? `/prop-sim/trade/${target.id}/sltp`
      : `/prop-sim/limit-order/${target.id}/sltp`;
    setBusy(true);
    try {
      const { data } = await api.post(url, body);
      toast.success("SL / TP updated");
      if (data.state && onStateUpdate) onStateUpdate(data.state);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const clearBoth = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const url = target.kind === "position"
        ? `/prop-sim/trade/${target.id}/sltp`
        : `/prop-sim/limit-order/${target.id}/sltp`;
      const { data } = await api.post(url, { stop_loss: null, take_profit: null });
      toast.success("SL / TP cleared");
      if (data.state && onStateUpdate) onStateUpdate(data.state);
      setSl(""); setTp("");
    } catch (e) {
      toast.error("Clear failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          // Unified UX: if this symbol has an open position, jump straight
          // into the full Exness Modify dialog on that trade. Otherwise
          // fall back to the compact popover for setting SL/TP on the
          // next order.
          const openPos = (positions || []).find(p => p.symbol === symbol && p.status === "open");
          if (openPos) {
            try { window.dispatchEvent(new CustomEvent("tj:open-modify", { detail: { tradeId: openPos.id } })); } catch (_) {}
            return;
          }
          setOpen(o => !o);
        }}
        data-testid="propsim-chart-sltp-add"
        className="absolute top-14 right-3 z-20 pointer-events-auto flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-500/90 hover:bg-emerald-500 text-black shadow-lg border border-emerald-300/30 backdrop-blur-md transition-all"
        style={{ transform: open ? "translateY(0)" : undefined }}>
        <Plus size={12} weight="bold" /> SL / TP
      </button>
      {open && (
        <div
          className="absolute top-24 right-3 z-30 w-[300px] rounded-lg border border-white/15 bg-neutral-950/95 backdrop-blur-xl shadow-2xl p-3 space-y-2.5"
          data-testid="propsim-chart-sltp-popover"
        >
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">
              Add SL / TP · {symbol}
            </p>
            <button
              onClick={() => setOpen(false)}
              className="text-white/50 hover:text-white p-1 rounded"
              data-testid="propsim-chart-sltp-close">
              <X size={12} />
            </button>
          </div>

          {targets.length === 0 ? (
            <div className="rounded-md border border-dashed border-white/10 bg-black/40 p-3 text-[11px] text-white/60" data-testid="propsim-chart-sltp-empty">
              No open positions or pending orders on <span className="font-mono text-white">{symbol}</span>.
              Place an order first, then come back to attach a stop-loss or take-profit.
            </div>
          ) : (
            <>
              <div>
                <p className="text-[9px] uppercase tracking-widest text-white/40 mb-1">Target</p>
                <select
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  data-testid="propsim-chart-sltp-target"
                  className="w-full h-8 rounded-md bg-black/60 border border-white/10 text-white text-xs px-2 focus:outline-none focus:border-emerald-500/60">
                  {targets.map(t => (
                    <option key={t.id} value={t.id} className="bg-black">
                      {t.kind === "position" ? "OPEN · " : "PENDING · "}{t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-red-400/80 mb-1 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-sm bg-red-500" /> Stop Loss
                  </p>
                  <input
                    type="number"
                    step="any"
                    value={sl}
                    onChange={(e) => setSl(e.target.value)}
                    placeholder="—"
                    data-testid="propsim-chart-sltp-sl"
                    className="w-full h-8 rounded-md bg-black/60 border border-red-500/25 text-white text-xs font-mono-num px-2 focus:outline-none focus:border-red-500/60" />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-emerald-400/80 mb-1 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-sm bg-emerald-500" /> Take Profit
                  </p>
                  <input
                    type="number"
                    step="any"
                    value={tp}
                    onChange={(e) => setTp(e.target.value)}
                    placeholder="—"
                    data-testid="propsim-chart-sltp-tp"
                    className="w-full h-8 rounded-md bg-black/60 border border-emerald-500/25 text-white text-xs font-mono-num px-2 focus:outline-none focus:border-emerald-500/60" />
                </div>
              </div>

              {target && (
                <p className="text-[10px] text-white/40 font-mono">
                  Reference: {fmt(target.ref_price, decimals)} · {target.side.toUpperCase()}
                </p>
              )}

              <div className="flex items-center gap-1.5 pt-1">
                <button
                  onClick={submit}
                  disabled={busy}
                  data-testid="propsim-chart-sltp-apply"
                  className="flex-1 h-8 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50">
                  <Check size={12} weight="bold" /> {busy ? "Saving…" : "Apply"}
                </button>
                <button
                  onClick={clearBoth}
                  disabled={busy || (!target?.stop_loss && !target?.take_profit)}
                  data-testid="propsim-chart-sltp-clear"
                  className="h-8 px-2.5 rounded-md border border-white/10 text-white/70 hover:text-white text-[11px] disabled:opacity-30">
                  Clear
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function ActiveDashboard({ state, symbols, onRefresh, onReset, onAbandon, onOrderPlaced, onShare }) {
  const c = state.challenge;
  const rules = c.rules;
  const targetPct = rules.profit_target_pct;
  const [symbol, setSymbol] = useState("EURUSD");

  const targetRatio = Math.min(100, Math.max(0, (state.pct_return / targetPct) * 100));
  const ddRatio = Math.min(100, (state.max_dd_pct / rules.max_total_drawdown_pct) * 100);
  const daysRatio = rules.min_trading_days > 0
    ? Math.min(100, (state.days_traded / rules.min_trading_days) * 100) : 100;
  const dailyUsed = Math.max(0, -state.today_pnl);
  const dailyRatio = Math.min(100, (dailyUsed / state.limits.max_daily_loss) * 100);

  const equityCurve = useMemo(() => {
    const rows = [{ date: c.started_at?.slice(0, 10), equity: state.starting_balance }];
    let running = state.starting_balance;
    const closed = [...state.closed_trades].sort((a, b) => (a.exit_time || "").localeCompare(b.exit_time || ""));
    for (const t of closed) {
      running += Number(t.pnl || 0);
      rows.push({ date: (t.exit_time || "").slice(0, 16).replace("T", " "), equity: Math.round(running * 100) / 100 });
    }
    rows.push({ date: "now", equity: state.current_equity });
    return rows;
  }, [state, c.started_at]);

  // ---- Bulk close handlers shared by OrderPanel (left) and OpenPositions table
  // NOTE: every close-path dispatches `tj:positions-updated` so the chart
  // component listens and wipes stale entry/SL/TP marks (bug user hit: after
  // 100% close the ENTRY band stayed on the chart forever).
  const closeAll = useCallback(() => api.post(`/prop-sim/close-all`).then(({ data }) => {
    toast.success(`Closed ${data.closed} positions`);
    onOrderPlaced(data.state);
    try { window.dispatchEvent(new CustomEvent("tj:positions-updated")); } catch (_) {}
  }).catch(() => toast.error("Close-all failed")), [onOrderPlaced]);
  const closeLosing = useCallback(() => api.post(`/prop-sim/close-losing`).then(({ data }) => {
    toast.success(`Closed ${data.closed} losing trade${data.closed !== 1 ? "s" : ""}`);
    onOrderPlaced(data.state);
    try { window.dispatchEvent(new CustomEvent("tj:positions-updated")); } catch (_) {}
  }).catch(() => toast.error("Close-losing failed")), [onOrderPlaced]);
  const closeProfitable = useCallback(() => api.post(`/prop-sim/close-profitable`).then(({ data }) => {
    toast.success(`Closed ${data.closed} winning trade${data.closed !== 1 ? "s" : ""}`);
    onOrderPlaced(data.state);
    try { window.dispatchEvent(new CustomEvent("tj:positions-updated")); } catch (_) {}
  }).catch(() => toast.error("Close-profitable failed")), [onOrderPlaced]);

  return (
    <div data-testid="propsim-active">
      {/* Breach → Zero banner (MFTC "wipe to $0" flow) */}
      {state.breach_zeroed && (
        <div
          data-testid="propsim-breach-zero-banner"
          className="mb-6 rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-950/60 via-red-900/30 to-neutral-950 p-6 lg:p-8 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ background: "radial-gradient(60% 60% at 20% 30%, #ef4444 0%, transparent 60%)" }} />
          <div className="relative flex flex-col lg:flex-row items-start lg:items-center gap-4">
            <div className="grid place-items-center w-14 h-14 rounded-xl bg-red-500/20 border border-red-500/40 shrink-0">
              <XCircle size={28} weight="fill" className="text-red-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] tracking-[0.3em] uppercase text-red-400 font-bold">MFTC · House rules breach</p>
              <p className="font-display text-2xl lg:text-3xl font-bold text-white mt-1">
                Account wiped to $0
              </p>
              <p className="text-sm text-red-200/90 mt-1.5 max-w-2xl">
                {c.breach_reason || state.violations?.[0] || "You breached a house rule."}
                {" "}Your balance has been reset to zero. Start again from zero to keep learning under real prop-firm consequences.
              </p>
            </div>
            <Button
              onClick={onReset}
              className="rounded-full bg-red-500 hover:bg-red-400 text-white font-bold shrink-0"
              data-testid="propsim-restart-zero-btn">
              <ArrowsClockwise size={14} weight="bold" className="mr-1.5" />
              Start again from zero
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="rounded-2xl border border-border bg-card p-3 sm:p-6 mb-4 sm:mb-6 flex flex-wrap items-center gap-3 sm:gap-6">
        <div className="grid place-items-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl shrink-0"
          style={{ background: `linear-gradient(135deg, ${c.firm_logo_color}, ${c.firm_logo_color}80)` }}>
          <Trophy size={20} weight="fill" className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-muted-foreground">Simulated challenge</p>
          <p className="font-display text-base sm:text-2xl font-bold truncate" data-testid="propsim-firm-label">
            {c.firm_name} · {c.challenge_label}
          </p>
          <p className="text-[11px] sm:text-xs text-muted-foreground truncate">
            {c.phase} · Started {c.started_at?.slice(0, 10)} · ${fmt(state.starting_balance, 0)} virtual
            {c.trader_name ? ` · ${c.trader_name}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusPill status={state.status} />
          <Button variant="outline" onClick={onShare} className="rounded-full" data-testid="propsim-share-btn">
            <Share size={14} className="mr-1.5" /> Share P&amp;L
          </Button>
          {state.status !== "active" ? (
            <Button onClick={onReset} className="rounded-full" data-testid="propsim-reset-btn">
              <ArrowsClockwise size={14} className="mr-1.5" /> Reset & try again
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onReset} className="rounded-full" data-testid="propsim-reset-btn">
                <ArrowsClockwise size={14} className="mr-1.5" /> Reset
              </Button>
              <Button variant="ghost" onClick={onAbandon} className="text-red-500 rounded-full" data-testid="propsim-abandon-btn">
                Abandon
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Chart on the LEFT (fills remaining width) + Order panel pinned to
          the RIGHT — matches Exness terminal layout so the chart gets the
          bulk of the horizontal real estate. On mobile (<xl) the order panel
          slides up from the bottom via a Sheet triggered by the FAB button. */}
      <div className="mb-4 grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4 h-[65vh] xl:h-[calc(100vh-180px)] min-h-[420px] xl:min-h-[720px]" data-testid="propsim-terminal-split">
        <TradingTerminal
          symbols={symbols}
          symbol={symbol}
          setSymbol={setSymbol}
          openTrades={state.open_trades}
          pendingOrders={state.pending_orders}
          onStateUpdate={onOrderPlaced}
          livePnl={{
            today: state.today_pnl,
            open: state.unrealized_pnl,
            total: state.total_pnl,
          }} />
        {/* Desktop-only inline OrderPanel */}
        <div className="hidden xl:block">
          <OrderPanel
            symbols={symbols}
            activeState={state}
            onOrderPlaced={onOrderPlaced}
            symbol={symbol}
            setSymbol={setSymbol}
            openTrades={state.open_trades}
            pendingOrders={state.pending_orders}
            onCloseAll={closeAll}
            onCloseLosing={closeLosing}
            onCloseProfitable={closeProfitable} />
        </div>
      </div>

      {/* Mobile-only FABs stacked bottom-right: Portfolio + Trade
          (Exness-mobile pattern). Portfolio opens a bottom-sheet with the
          Open / Pending / Closed lists + inline SL/TP modify. Trade opens
          the full OrderPanel bottom-sheet. Both hidden on ≥ xl. */}
      <div className="xl:hidden fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2">
        <MobilePortfolioSheet
          state={state}
          symbols={symbols}
          onStateUpdate={onOrderPlaced} />
        <Sheet>
          <SheetTrigger asChild>
            <button
              data-testid="propsim-mobile-trade-fab"
              className="rounded-full bg-primary text-primary-foreground shadow-2xl px-5 py-3 font-semibold text-sm flex items-center gap-2 hover:opacity-90 transition-opacity">
              <Lightning size={16} weight="fill" /> Trade
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            style={{ zIndex: 300 }}
            className="p-0 h-[90vh] max-h-[90vh] overflow-y-auto rounded-t-2xl border-t border-border"
            data-testid="propsim-mobile-order-sheet">
            <div className="p-4">
              <OrderPanel
                symbols={symbols}
                activeState={state}
                onOrderPlaced={onOrderPlaced}
                symbol={symbol}
                setSymbol={setSymbol}
                openTrades={state.open_trades}
                pendingOrders={state.pending_orders}
                onCloseAll={closeAll}
                onCloseLosing={closeLosing}
                onCloseProfitable={closeProfitable} />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Open positions — moved here directly below the chart so
          managing SL/TP + closes is one glance away, no scrolling. */}
      <div className="mb-4">
        <OpenPositions
          open={state.open_trades}
          meta={symbols}
          onClose={(id, qty) => api.post(`/prop-sim/close/${id}`, qty != null ? { qty } : {}).then(({ data }) => {
            toast.success(
              data.partial
                ? `Partial close · ${fmt(data.closed_qty, 2)} lots · ${money(data.pnl, true)} · ${fmt(data.remaining_qty, 2)} left`
                : `Closed · ${money(data.pnl, true)}`
            );
            onOrderPlaced(data.state);
            // Only 100% closes should wipe the on-chart marks — a partial
            // close keeps the position open at reduced qty, so the entry
            // ribbon must stay put. The event fires either way; the chart's
            // handler calls drawActivePositionLine which re-fetches
            // /prop-sim/positions and paints whatever's actually open.
            try { window.dispatchEvent(new CustomEvent("tj:positions-updated")); } catch (_) {}
          }).catch(() => toast.error("Close failed"))}
          onCloseAll={closeAll}
          onCloseLosing={closeLosing}
          onCloseProfitable={closeProfitable}
        />
      </div>

      {/* SL/TP hint — points users to the on-chart "+" button */}
      <div className="mb-4 rounded-lg border border-white/5 bg-secondary/30 px-4 py-2.5 text-[11px] text-muted-foreground flex items-center gap-2" data-testid="propsim-drag-note">
        <Plus size={13} weight="bold" className="text-emerald-400 shrink-0" />
        <span>
          Tap the <span className="text-emerald-400 font-semibold">+ SL / TP</span> button on the top-right of the chart to attach or edit stops &amp; targets on any open position or pending limit order — updates instantly on the chart ribbons.
        </span>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
        <div className="rounded-xl border border-border bg-card p-3 sm:p-4" data-testid="propsim-kpi-equity">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Equity</p>
          <p className="font-mono-num text-lg sm:text-2xl font-bold mt-1 sm:mt-1.5 truncate">{money(state.current_equity)}</p>
          <p className={`text-[10px] sm:text-[11px] mt-0.5 sm:mt-1 truncate ${state.total_pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>
            {money(state.total_pnl, true)} ({state.pct_return >= 0 ? "+" : ""}{state.pct_return}%)
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3 sm:p-4" data-testid="propsim-kpi-today">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Today's P&amp;L</p>
          <p className={`font-mono-num text-lg sm:text-2xl font-bold mt-1 sm:mt-1.5 truncate ${state.today_pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>
            {money(state.today_pnl, true)}
          </p>
          <p className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5 sm:mt-1">{state.today_pnl_pct}% of balance</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3 sm:p-4" data-testid="propsim-kpi-unrealized">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Open P&amp;L</p>
          <p className={`font-mono-num text-lg sm:text-2xl font-bold mt-1 sm:mt-1.5 truncate ${state.unrealized_pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>
            {money(state.unrealized_pnl, true)}
          </p>
          <p className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5 sm:mt-1">{state.open_trades?.length || 0} open positions</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3 sm:p-4" data-testid="propsim-kpi-deadline">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Deadline</p>
          <p className="font-mono-num text-lg sm:text-2xl font-bold mt-1 sm:mt-1.5">
            {state.days_left != null ? `${state.days_left}d` : "None"}
          </p>
          <p className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5 sm:mt-1 truncate">
            {c.deadline_at ? `by ${c.deadline_at.slice(0, 10)}` : "No time limit"}
          </p>
        </div>
      </div>

      {/* Rule progress */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <ProgressCard
          title="Profit target" icon={Target}
          value={`${state.pct_return}%`}
          target={`of ${targetPct}%`}
          pct={targetRatio}
          tone={targetRatio >= 100 ? "green" : "blue"}
          subtitle={`${money(state.total_pnl)} of ${money(state.limits.profit_target)}`}
          testid="propsim-progress-target" />
        <ProgressCard
          title="Daily loss headroom" icon={Warning}
          value={money(Math.max(0, state.limits.max_daily_loss - Math.max(0, -state.today_pnl)))}
          target={`limit ${state.limits.max_daily_loss ? "$" + fmt(state.limits.max_daily_loss, 0) : "—"}`}
          pct={dailyRatio}
          tone={dailyRatio >= 80 ? "red" : dailyRatio >= 50 ? "amber" : "green"}
          subtitle={`Used ${money(Math.max(0, -state.today_pnl))} today (${rules.max_daily_loss_pct}%)`}
          testid="propsim-progress-daily" />
        <ProgressCard
          title="Max drawdown" icon={TrendDown}
          value={`${state.max_dd_pct}%`}
          target={`of ${rules.max_total_drawdown_pct}%`}
          pct={ddRatio}
          tone={ddRatio >= 80 ? "red" : ddRatio >= 50 ? "amber" : "green"}
          subtitle={rules.hwm_drawdown ? "measured from high-watermark" : "measured from start balance"}
          testid="propsim-progress-dd" />
        <ProgressCard
          title="Min trading days" icon={Calendar}
          value={state.days_traded}
          target={`of ${rules.min_trading_days || 0}`}
          pct={daysRatio}
          tone={daysRatio >= 100 ? "green" : "amber"}
          subtitle={daysRatio >= 100 ? "Requirement met" : "keep placing at least 1 trade per day"}
          testid="propsim-progress-days" />
        {state.consistency?.enforced ? (
          <ConsistencyCard c={state.consistency} />
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-card/40 p-4" data-testid="propsim-consistency-off">
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Consistency rule</p>
            <p className="font-mono-num text-2xl font-bold mt-1.5 text-muted-foreground">N/A</p>
            <p className="text-[11px] text-muted-foreground mt-1">{c.firm_name} doesn't enforce this rule</p>
          </div>
        )}
      </div>

      {/* Consistency detail badge — surfaces the "target hit but blocked by consistency" reason */}
      {state.pending_reasons?.length > 0 && (
        <div className="mb-4 space-y-2">
          {state.pending_reasons.map((r, i) => (
            <div key={i} className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-3 flex items-start gap-2 text-sm text-blue-500" data-testid={`propsim-pending-${i}`}>
              <Timer size={16} weight="fill" className="shrink-0 mt-0.5" />
              <span>{r}</span>
            </div>
          ))}
        </div>
      )}

      {/* Payout projection (only when passed) */}
      {state.status === "passed" && (
        <div className="mb-4">
          <PayoutCard p={state.payout_projection} firmName={c.firm_name} challengeLabel={c.challenge_label} />
        </div>
      )}

      {/* Violations / warnings */}
      {(state.violations.length > 0 || state.warnings.length > 0) && (
        <div className="mb-4 space-y-2">
          {state.violations.map((v, i) => (
            <div key={i} className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 flex items-center gap-2 text-sm text-red-500" data-testid={`propsim-violation-${i}`}>
              <XCircle size={18} weight="fill" /> <span className="font-medium">Rule breach:</span> {v}
            </div>
          ))}
          {state.warnings.map((w, i) => (
            <div key={i} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-2 text-sm text-amber-500" data-testid={`propsim-warning-${i}`}>
              <Warning size={18} weight="fill" /> {w}
            </div>
          ))}
        </div>
      )}

      {/* Pending orders + closed trades. OpenPositions moved above (right
          below the chart) for one-glance access. */}
      <div className="grid xl:grid-cols-1 gap-4">
        <div className="space-y-4">
          <PendingOrders
            pending={state.pending_orders}
            meta={symbols}
            onCancel={(id) => api.post(`/prop-sim/limit-order/${id}/cancel`).then(({ data }) => {
              toast.success("Limit order cancelled");
              if (data.state) onOrderPlaced(data.state);
            }).catch(() => toast.error("Cancel failed"))}
          />
          <ClosedTradesTable closed={state.closed_trades} meta={symbols} />
        </div>
      </div>

      {/* Equity curve */}
      <div className="mt-4 rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Equity curve</p>
          <p className="text-xs text-muted-foreground">
            <Timer size={11} className="inline" /> Auto-refresh · <button onClick={onRefresh} className="hover:text-foreground underline">refresh now</button>
          </p>
        </div>
        <div className="h-72" data-testid="propsim-equity-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={equityCurve}>
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
              <YAxis domain={["auto", "auto"]} stroke="hsl(var(--muted-foreground))" fontSize={10}
                tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
              <Tooltip formatter={(v) => money(v)}
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <ReferenceLine y={state.starting_balance} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" label={{ value: "start", position: "insideLeft", fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
              <ReferenceLine y={state.starting_balance * (1 + targetPct / 100)} stroke="#10b981" strokeDasharray="3 3" label={{ value: "target", position: "insideRight", fill: "#10b981", fontSize: 10 }} />
              <ReferenceLine y={state.starting_balance * (1 - rules.max_total_drawdown_pct / 100)} stroke="#ef4444" strokeDasharray="3 3" label={{ value: "max DD", position: "insideRight", fill: "#ef4444", fontSize: 10 }} />
              <Line type="monotone" dataKey="equity" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Community leaderboard */}
      <div className="mt-6">
        <Leaderboard />
      </div>
    </div>
  );
}

// -------------- Page --------------
export default function PropChallenge() {
  const [firms, setFirms] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [pending, setPending] = useState(null); // { firm_id, challenge_id }
  const [me, setMe] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/prop-sim/active");
      setActive(data.active);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/prop-sim/firms").then(r => setFirms(r.data)),
      api.get("/prop-sim/symbols").then(r => setSymbols(r.data)),
      api.get("/auth/me").then(r => setMe(r.data)).catch(() => {}),
      refresh(),
    ]).finally(() => setLoading(false));
  }, [refresh]);

  useEffect(() => {
    if (!active || active.status !== "active") return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 15000);
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [active, refresh]);

  // Step 1 — user picks firm + challenge → open the trader-name form
  const start = (firm_id, challenge_id) => {
    if (!challenge_id) return toast.error("Pick a challenge tier");
    setPending({ firm_id, challenge_id });
  };

  // Step 2 — form submitted → actually POST /prop-sim/start with identity
  const submitStart = async (identity) => {
    if (!pending) return;
    setStarting(true);
    try {
      const { data } = await api.post("/prop-sim/start", { ...pending, ...identity });
      setActive(data);
      setPending(null);
      toast.success(`Challenge started — good luck, ${identity.trader_name}!`);
      if (shouldShowTutorial()) setTutorialOpen(true);
    } catch (e) {
      ruleToast(e, "Could not start challenge");
    } finally { setStarting(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the challenge with a fresh balance?")) return;
    try {
      const { data } = await api.post("/prop-sim/reset");
      setActive(data);
      toast.success("Challenge reset — start again from zero");
    } catch { toast.error("Reset failed"); }
  };

  const abandon = async () => {
    if (!window.confirm("Abandon this challenge? You can start a new one after.")) return;
    try {
      await api.post("/prop-sim/abandon");
      setActive(null);
      toast.success("Abandoned");
    } catch { toast.error("Abandon failed"); }
  };

  const share = async () => {
    try {
      const { data } = await api.post("/prop-sim/share");
      const url = `${window.location.origin}${data.url}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard", { description: url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    }
  };

  if (loading) return (
    <div className="p-6 lg:p-8 w-full text-center text-muted-foreground">
      Loading Tradelog Prop Firm Challenge…
    </div>
  );

  return (
    <div className={`w-full ${active ? "px-3 lg:px-4 py-4" : "p-4 sm:p-6 lg:p-10 max-w-[1400px] mx-auto"}`} data-testid="propsim-page">
      <PropTutorial open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
      <TraderNameForm
        open={!!pending}
        submitting={starting}
        defaults={{
          name: me?.name || "",
          country: me?.country || "",
          handle: me?.handle || "",
        }}
        challengeRules={
          pending
            ? firms
                .find((f) => f.id === pending.firm_id)
                ?.challenges?.find((c) => c.id === pending.challenge_id)?.rules
            : null
        }
        onSubmit={submitStart}
        onCancel={() => setPending(null)}
      />
      {active ? (
        <ActiveDashboard
          state={active}
          symbols={symbols}
          onRefresh={refresh}
          onReset={reset}
          onAbandon={abandon}
          onOrderPlaced={setActive}
          onShare={share} />
      ) : (
        <>
          <Landing firms={firms} onStart={start} starting={starting} />
          <div className="mt-8">
            <Leaderboard />
          </div>
        </>
      )}
    </div>
  );
}
